import { CompanyId, GroupId, ProjectId } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/**
 * Enum for general archiving states of projects. This is only used in the dashboard
 * and does not refer to the API response, for that look at ArchivingState.
 */
export enum ProjectArchivingState {
  /** State of projects that will be shown in the active projects tab */
  active = "active",

  /** State of projects that will be shown in the archived projects tab */
  archived = "archived",
}

/** Base type for project */
export interface BaseProjectProps {
  /** A single project to be passed as a prop */
  project: SdbProject;
}

/** Base type for list of projects */
export interface BaseProjectsProps {
  /** List of projects to be passed as a prop */
  projects: SdbProject[];
}

/** Base type for list of projects */
export interface BaseProjectsMapProps {
  /** A map of projects by its id to be passed as a prop */
  projectsMap: Map<ProjectId, SdbProject>;
}

/** Project access level to display in readable text */
export const AccessLevelDisplayNames: {
  [key in FaroCoreApiTypes.ProjectAccessLevel]: string;
} = {
  ["private"]: "Private",
  ["unlisted"]: "Via link",
};

/** Project Settings type */
export type ProjectSettings = {
  /** Whether the markups/annotations should be visible for project viewers */
  shouldDisplayMarkupsForViewers?: boolean;
};

/** Type for the group that the project belongs to */
export type ProjectGroup = {
  /** Group ID */
  id: GroupId;

  /** Group name */
  name: string;
};

/**
 * This type covers all project types returned from different routes
 * The purpose of it is to remove the confusion between all these types
 */
export type SdbProject = Pick<
  FaroCoreApiTypes.CompanyProject,
  | "accessLevel"
  | "archivingState"
  | "address"
  | "createdAt"
  | "id"
  | "modifiedAt"
  | "name"
  | "permissions"
  | "tags"
  | "storage"
> &
  Pick<FaroCoreApiTypes.SearchedProject, "area"> & {
    /** The name of the project's client */
    clientName: string | null;

    /** Any description for the project. It will only be shown if available */
    description: string | null;

    /** Evaluation data of the project */
    evaluation: FaroCoreApiTypes.Evaluation | null;

    /** List of all the features that can be assigned to a project and whether they are enabled or not */
    features: FaroCoreApiTypes.FeatureFlags | null;

    /**
     * All the features that are available based on existing plans with flags showing whether
     * they are enabled and permitted or not
     */
    featuresAvailable: FaroCoreApiTypes.Feature[] | null;

    /** Points to the location where the zip file of the projectContainer is stored */
    fileUrl: string | null;

    /** The group in which the project belongs to */
    group: ProjectGroup | null;

    /** Timestamp of last project's capture */
    lastCapturedAt: FaroCoreApiTypes.UnixTimestamp | null;

    /** Timestamp of last project download */
    lastDownloadedAt: FaroCoreApiTypes.UnixTimestamp | null;

    /** The user and company assigned as managers to the project */
    managers: {
      /** The project manager */
      projectManager: FaroCoreApiTypes.UserAsManager | null;

      /** The company manager */
      companyManager: FaroCoreApiTypes.UserAsManager | null;
    };

    /**
     * The users added to the project
     * This property is passed to the server response only if the requesting user has the project admin role
     */
    members: FaroCoreApiTypes.ProjectMemberBase[];

    /** Role of this user on the project */
    role: FaroCoreApiTypes.ProjectRole | null;

    /** Whether or not the project is standalone */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    standalone: boolean | null;

    /** Link to the project thumbnail */
    thumbnailUrl: string | null;
  };

  /** Fields used for projects pagination, sorting and searching */
  export interface PaginatedProjects {
    /** ID of the company that contains the projects */
  companyId: CompanyId;

  /** List of projects */
  projects: SdbProject[];

  /** List of project IDs */
  ids: ProjectId[];

  /** Whether projects are currently being fetched from the backend */
  isFetchingProjects: boolean;

  /** Whether the first page of projects has been fetched */
  hasFetchedFirstPage: boolean;

  /** The archiving state of the projects */
  projectArchivingState: ProjectArchivingState;

  /** Function to fetch the next page of projects */
  loadMoreProjects: () => void;

  /** Projects sort criteria */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;

  /** Function to set the projects sort criteria */
  setSortBy: (sortBy: FaroCoreApiTypes.ProjectsSortBy) => void;

  /** Current page number of the projects table */
  page: number;

  /** Function to set the current page number of the projects table */
  setPage: (page: number) => void;

  /** Current search query used to filter the projects */
  search?: string;

  /** Whether the user is currently typing a projects search query */
  isDebouncingSearch: boolean;

  /** Cursor that can be used to fetch the next page of projects */
  nextPageCursor?: string;

  /** ID of the group that contains the projects, if applicable */
  groupId?: GroupId;
}
